import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import InsightCard from './cards/InsightCard';
import { withWidgetSpacingWrapper } from '../shared/WidgetSpacingWrapper';
import WidgetIdWrapper from '../shared/WidgetIdWrapper';
import { device } from '../../utils/styled-component-mixins';
import useMediaQuery from '../custom-hooks/useMediaQuery';

const CommunityInvolvementWrapperStyled = styled.div`
  background: linear-gradient(
    360deg,
    rgba(99, 120, 138, 0.3) 0%,
    rgba(99, 120, 138, 0.3) 0.01%,
    rgba(99, 120, 138, 0) 100%
  );
  position: relative;

  &:before {
    content: '';
    z-index: -1;

    @media ${device.desktop} {
      background-color: var(--white);
      clip-path: polygon(100% 0, 0 10%, 0 0);
      height: 101%;
      position: absolute;
      right: 0;
      top: -0.1rem;
      width: 100%;
    }
  }

  &:after {
    content: '';
    z-index: -1;

    @media ${device.desktop} {
      background-color: var(--white);
      clip-path: polygon(0 96%, 100% 90%, 100% 100%, 0% 100%);
      height: 101%;
      position: absolute;
      right: 0;
      top: 0;
      width: 100%;
    }
  }
  @media ${device.desktop} {
    ${(props) => props.backgroundImage && `background-image: url('${props.backgroundImage}');`}
    background-position: center;
    background-size: cover;
    display: block;
    z-index: 0;

    &:after {
      clip-path: polygon(0 96%, 100% 70%, 100% 100%, 0% 100%);
      z-index: -1;
    }

    &:before {
      clip-path: polygon(100% 0, 0 30%, 0 0);
      z-index: -1;
    }

    & > div {
      position: relative;

      .btn-wrapper {
        bottom: 2.5rem;
        position: absolute;
        right: 11rem;
      }
    }
  }

  .community-involvement-description {
    margin-bottom: 7.2rem;

    > * {
      font-size: 1.8rem;
      line-height: 2.4rem;
      text-align: center;

      @media ${device.desktop} {
        font-size: 2rem;
        line-height: 3.2rem;
      }
    }
  }

  .card-section {
    display: flex;
    filter: drop-shadow(0px 16px 80px rgba(91, 110, 127, 0.25));
    flex-direction: row;
    justify-content: center;
    margin-bottom: 2.5rem;
    position: relative;
    z-index: 100;

    @media ${device.desktop} {
      margin-bottom: 0;

      > div {
        flex: 0 0 calc(33.33% - 1.6rem);
        margin-right: 1.6rem;
        height: unset;

        &:last-child {
          margin-right: 0;
        }
      }
    }

    .swiper {
      flex: 1;
    }

    .insight-card {
      box-shadow: none;
      display: flex;
      flex-direction: column;

      @media ${device.tablet} {
        display: grid;
        grid: 1fr 1fr / 1fr;
      }

      &-wrapper {
        height: 100%;
      }

      .featured-card-content {
        display: flex;
        flex: 1;
      }
      .animation-wrapper {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .content-wrapper {
          .animated-content {
            p {
              display: block;
            }
          }
        }
      }
    }
  }

  .btn-wrapper {
    display: flex;
    justify-content: center;
    position: relative;
    z-index: 100;

    @media ${device.desktop} {
      display: block;
    }
  }

  .swiper-slide {
    > div {
      height: 100%;
      margin-bottom: 0;
    }
  }
`;

const HeaderWrapperStyled = styled.div`
  h2 {
    font-size: 3rem;
    font-weight: normal;
    line-height: 3.2rem;
    margin-bottom: 1.6rem;
    text-align: center;

    @media ${device.desktop} {
      font-size: 3.6rem;
      line-height: 4.8rem;
    }
  }
`;

const CommunityInvolvementComponent = (props) => {
  const { properties, items } = props;
  const cardProps = {
    layout: 'standard',
    mediaSide: 'left',
    widgetSpacingWrapperProps: { marginBottomMobile: '0rem' }
  };
  const isDesktop = useMediaQuery(device.desktop);
  return (
    <WidgetIdWrapper widgetID={properties.widgetID}>
      <HeaderWrapperStyled className="container container-padding-default">
        <h2>{properties.header}</h2>
      </HeaderWrapperStyled>
      <CommunityInvolvementWrapperStyled
        className={`card-swiper ${!isDesktop ? 'angled-background' : ''}`}
        backgroundImage={properties.backgroundImage}
      >
        {isDesktop ? (
          <div className="container container-padding-default">
            <div
              className="community-involvement-description"
              dangerouslySetInnerHTML={{ __html: properties.description }}
            ></div>
            <div>
              <div className="card-section">
                {items.map((item, index) => (
                  <InsightCard
                    key={`${item.pageTitle}-${index}`}
                    cardProps={cardProps}
                    hidden={false}
                    item={{
                      ...item,
                      thumbnail1: item.thumbnail,
                      readMoreText: item.linkText,
                      url: item.linkUrl,
                      hidden: false
                    }}
                    urlTarget={item.linkTarget}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="card-slider">
            <div className="container container-padding-default">
              <div
                className="community-involvement-description"
                dangerouslySetInnerHTML={{ __html: properties.description }}
              ></div>
            </div>
            <div className="card-section">
              <Swiper
                pagination={{ clickable: false }}
                centeredSlides={true}
                slidesPerView="auto"
                spaceBetween={16}
                modules={[Pagination]}
                init={false}
              >
                {items.map((item, index) => (
                  <SwiperSlide key={index}>
                    <InsightCard
                      key={`${item.pageTitle}-${index}`}
                      cardProps={cardProps}
                      item={{
                        ...item,
                        thumbnail1: item.thumbnail,
                        readMoreText: item.linkText,
                        url: item.linkUrl,
                        hidden: false
                      }}
                      urlTarget={item.linkTarget}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        )}
      </CommunityInvolvementWrapperStyled>
    </WidgetIdWrapper>
  );
};

export default withWidgetSpacingWrapper(CommunityInvolvementComponent);
